/**
 * ZOOM Y ENCUADRE del lienzo.
 *
 * Un texto de 4 mm —el minimo que admite el DTF— mide dos pixeles en pantalla
 * cuando la zona entera cabe en el movil. Sin acercar no hay forma de comprobar
 * el detalle que justamente separa lo bien impreso de lo mal impreso.
 *
 * Aritmetica PURA: ni Fabric, ni DOM, ni eventos. La vista es un factor de
 * escala mas un desplazamiento, y todo lo demas —rueda, pinza, doble
 * pulsacion— acaba llamando a una de estas funciones. Asi se puede probar el
 * comportamiento sin abrir un navegador, que es donde estos calculos fallan de
 * formas dificiles de reproducir.
 */

export interface Vista {
  /** 1 = la zona entera cabe justo en el lienzo. */
  zoom: number;
  /** Desplazamiento del contenido, en pixeles de pantalla. */
  x: number;
  y: number;
}

/** Ni tan lejos que no se vea, ni tanto que se pierda de vista lo que se edita. */
export const ZOOM_MINIMO = 0.5;
export const ZOOM_MAXIMO = 8;

export const VISTA_INICIAL: Vista = { zoom: 1, x: 0, y: 0 };

export function limitarZoom(zoom: number): number {
  /*
    `NaN` e `Infinity` NO son el mismo caso.

    `Infinity` sale de acercar mucho o de una pinza que se abre de golpe: la
    direccion se conoce, asi que el maximo es la respuesta. `NaN` sale de una
    division 0/0 y no dice nada: ahi lo unico honesto es volver al encuadre de
    partida, porque quedarse en el maximo dejaria al usuario perdido dentro de
    su propio diseno sin saber por que.
  */
  if (Number.isNaN(zoom)) return 1;
  return Math.min(ZOOM_MAXIMO, Math.max(ZOOM_MINIMO, zoom));
}

/**
 * Acerca o aleja MANTENIENDO FIJO el punto bajo el cursor.
 *
 * Es la diferencia entre un zoom util y uno que marea: si se escala respecto al
 * centro, lo que el usuario esta mirando se le va de la pantalla y tiene que
 * volver a buscarlo despues de cada rueda.
 */
export function acercarEn(vista: Vista, factor: number, puntoX: number, puntoY: number): Vista {
  const zoom = limitarZoom(vista.zoom * factor);
  // El factor REAL puede no ser el pedido si se topo con el limite.
  const real = zoom / vista.zoom;
  return {
    zoom,
    x: puntoX - (puntoX - vista.x) * real,
    y: puntoY - (puntoY - vista.y) * real,
  };
}

/** Arrastre de la vista (espacio + raton, o dos dedos en el movil). */
export function desplazar(vista: Vista, dx: number, dy: number): Vista {
  return { ...vista, x: vista.x + dx, y: vista.y + dy };
}

/** Vuelve al encuadre de partida: la zona entera, centrada. */
export function ajustar(): Vista {
  return { ...VISTA_INICIAL };
}

/**
 * Zoom al que un milimetro se ve del tamano que tendra impreso.
 *
 * `pxPorMm` es la escala del lienzo cuando la zona cabe entera; `pixelesPorMm`
 * es lo que mide un milimetro real en ESTA pantalla. Sirve para el boton
 * «tamano real», que es la unica forma honesta de juzgar si un texto se va a
 * leer: en miniatura todo parece legible.
 */
export function zoomTamanoReal(pxPorMm: number, pixelesPorMmPantalla: number): number {
  if (pxPorMm <= 0) return 1;
  return limitarZoom(pixelesPorMmPantalla / pxPorMm);
}

/**
 * Distancia entre dos dedos, para la pinza.
 *
 * Se saca aparte porque el calculo se hace en cada movimiento y equivocarse
 * aqui produce un zoom que salta.
 */
export function distancia(
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number },
): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/** Punto medio entre dos dedos: es el centro sobre el que se acerca. */
export function puntoMedio(
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number },
): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/**
 * Si la vista esta en su sitio de partida.
 *
 * Se usa para no enseñar el boton de «ajustar» cuando no haria nada: un boton
 * que no hace nada al pulsarlo se lee como que la aplicacion no responde.
 */
export function esVistaInicial(vista: Vista): boolean {
  return Math.abs(vista.zoom - 1) < 0.001 && Math.abs(vista.x) < 0.5 && Math.abs(vista.y) < 0.5;
}
